/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode, TextareaHTMLAttributes } from "react";
import {
  ArrowUpDown,
  Car,
  BriefcaseBusiness,
  CalendarDays,
  Camera,
  ChevronDown,
  CheckCircle2,
  ClipboardList,
  Copy,
  CreditCard,
  Clock3,
  Home,
  Image as ImageIcon,
  LogOut,
  MessageCircle,
  Navigation2,
  Phone,
  Plus,
  Settings,
  Share2,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Upload,
  User,
  UserCircle,
  Users
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type {
  AuthMeDTO,
  ClientDTO,
  InvoiceDTO,
  JobChecklistItemDTO,
  JobDTO,
  LeadDTO,
  MediaAssetDTO,
  MessageTemplateDTO,
  OrganizationDTO,
  QuoteDTO,
  QuotePackageDTO,
  ServiceTargetDTO
} from "@tendvia/shared";
import { api } from "../api";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { ClientsPanel } from "./ClientsPanel";
import { Harbor } from "./Harbor";
import { SalesPanel } from "./SalesPanel";

type View = "home" | "clients" | "sales" | "jobs" | "profile" | "settings";

type AppShellProps = {
  me: AuthMeDTO;
  onLogout: () => void;
};

const primaryNav: Array<{ id: View; label: string; icon: LucideIcon }> = [
  { id: "home", label: "Today", icon: Home },
  { id: "clients", label: "Clients", icon: Users },
  { id: "sales", label: "Sales", icon: MessageCircle },
  { id: "jobs", label: "Jobs", icon: BriefcaseBusiness }
];

export function AppShell({ me, onLogout }: AppShellProps) {
  const [authMe, setAuthMe] = useState(me);
  const [view, setView] = useState<View>("home");
  const [clients, setClients] = useState<ClientDTO[]>([]);
  const [targets, setTargets] = useState<ServiceTargetDTO[]>([]);
  const [leads, setLeads] = useState<LeadDTO[]>([]);
  const [quotes, setQuotes] = useState<QuoteDTO[]>([]);
  const [quotePackages, setQuotePackages] = useState<QuotePackageDTO[]>([]);
  const [jobs, setJobs] = useState<JobDTO[]>([]);
  const [invoices, setInvoices] = useState<InvoiceDTO[]>([]);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplateDTO[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    setAuthMe(me);
  }, [me]);

  async function refreshData() {
    try {
      const [nextClients, nextTargets, nextLeads, nextQuotes, nextPackages, nextJobs, nextInvoices, nextTemplates] = await Promise.all([
        api.clients(),
        api.serviceTargets(),
        api.leads(),
        api.quotes(),
        api.quotePackages(),
        api.jobs(),
        api.invoices(),
        api.messageTemplates()
      ]);
      setClients(nextClients);
      setTargets(nextTargets);
      setLeads(nextLeads);
      setQuotes(nextQuotes);
      setQuotePackages(nextPackages);
      setJobs(nextJobs);
      setInvoices(nextInvoices);
      setMessageTemplates(nextTemplates);
    } catch {
      setClients([]);
      setTargets([]);
      setLeads([]);
      setQuotes([]);
      setQuotePackages([]);
      setJobs([]);
      setInvoices([]);
      setMessageTemplates([]);
    } finally {
      setLoadingData(false);
    }
  }

  useEffect(() => {
    void refreshData();
  }, []);

  async function logout() {
    await api.logout();
    onLogout();
  }

  if (window.location.pathname.startsWith("/harbor")) {
    return <Harbor />;
  }

  if (loadingData) {
    return (
      <main className="grid min-h-screen place-items-center bg-background px-4 text-text">
        <p className="rounded-pill border border-border/60 bg-surface px-4 py-3 text-sm font-bold">Loading TendVia</p>
      </main>
    );
  }

  return (
    <main className="min-h-dvh bg-background text-text">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-surface/92 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 pb-3 pt-[calc(0.875rem+env(safe-area-inset-top))]">
          <button
            className="flex min-w-0 items-center gap-3 rounded-pill pr-2 text-left"
                type="button"
                onClick={() => setView("home")}
              >
            <span className="inline-grid size-11 shrink-0 place-items-center rounded-full border border-border/50 bg-surface-soft text-primary">
              <Sparkles className="size-5" aria-hidden="true" />
            </span>
              <span className="min-w-0">
                <span className="block text-lg font-extrabold leading-tight">TendVia</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs font-bold text-muted">
                <span className="truncate">{authMe.organization.name}</span>
                <span className="rounded-pill border border-border/55 bg-surface-soft px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-primary">
                  {formatVerticalLabel(authMe.organization.vertical)}
                </span>
              </span>
            </span>
          </button>

          <div className="relative">
            <details className="group">
              <summary
                className="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-token border border-border/70 bg-elevated px-2.5 py-2 text-sm font-extrabold marker:hidden [&::-webkit-details-marker]:hidden"
                aria-label="Account menu"
              >
                <UserCircle className="size-5 text-primary" aria-hidden="true" />
                <span className="hidden max-w-28 truncate sm:block">{authMe.user.email}</span>
                <ChevronDown className="size-4 text-muted transition group-open:rotate-180" aria-hidden="true" />
              </summary>
              <div className="absolute right-0 mt-2 w-64 rounded-panel border border-border/70 bg-surface p-2 shadow-float">
                <AccountMenuButton icon={User} label="User profile" onClick={() => setView("profile")} />
                <AccountMenuButton icon={SlidersHorizontal} label="App settings" onClick={() => setView("settings")} />
                <AccountMenuButton icon={LogOut} label="Sign out" onClick={logout} />
              </div>
            </details>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-4 sm:pt-5">
        <section className="min-h-[70dvh] p-1 sm:p-2">
          {view === "home" ? <TodayPanel clients={clients} targets={targets} leads={leads} quotes={quotes} jobs={jobs} invoices={invoices} onOpenSales={() => setView("sales")} onOpenJobs={() => setView("jobs")} /> : null}
          {view === "clients" ? <ClientsPanel clients={clients} targets={targets} onRefresh={refreshData} /> : null}
          {view === "sales" ? <SalesPanel clients={clients} targets={targets} leads={leads} quotes={quotes} packages={quotePackages} jobs={jobs} onRefresh={refreshData} /> : null}
          {view === "jobs"
            ? (
                <JobsPanel
                  jobs={jobs}
                  invoices={invoices}
                  clients={clients}
                  targets={targets}
                  organization={authMe.organization}
                  messageTemplates={messageTemplates}
                  onRefresh={refreshData}
                />
              )
            : null}
          {view === "profile" ? <ProfilePanel me={authMe} /> : null}
          {view === "settings"
            ? (
                <SettingsPanel
                  me={authMe}
                  templates={messageTemplates}
                  onMeUpdated={setAuthMe}
                  onTemplatesUpdated={setMessageTemplates}
                />
              )
            : null}
        </section>
      </div>

      <footer className="fixed inset-x-0 bottom-0 z-30 border-t border-border/70 bg-surface/92 pb-[calc(0.625rem+env(safe-area-inset-bottom))] pt-2 shadow-dock backdrop-blur-xl">
        <nav className="mx-auto grid max-w-md grid-cols-4 gap-2 px-3 sm:max-w-lg" aria-label="Primary">
          {primaryNav.map((item) => {
            const Icon = item.icon;
            const active = view === item.id;
            return (
              <button
                key={item.id}
                className={`grid min-h-14 place-items-center gap-1 rounded-token border px-2 py-2 text-xs font-extrabold transition ${
                  active
                    ? "border-primary/10 bg-primary text-primary-contrast shadow-soft"
                    : "border-transparent text-muted hover:bg-surface-soft hover:text-text"
                }`}
                type="button"
                onClick={() => setView(item.id)}
              >
                <Icon className="size-5" aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </nav>
      </footer>
    </main>
  );
}

type TodayRouteKind = "job" | "follow-up";

type TodayRouteItem = {
  id: string;
  kind: TodayRouteKind;
  orderLabel: string;
  title: string;
  customerName: string;
  vehicleLabel: string;
  locationLabel: string;
  windowLabel: string;
  contextLabel: string;
  valueLabel: string;
  statusLabel: string;
  statusTone: "success" | "warning" | "info";
  chipLabels: string[];
  contactPhone: string | null;
  address: string | null;
  sortStart: string | null;
  primaryAction: { label: string; kind: "navigate" | "open-sales" | "open-jobs" };
  secondaryActions: Array<{ label: string; kind: "call" | "text" | "note" | "reorder" | "open-sales" | "open-jobs" }>;
};

function TodayPanel({
  clients,
  targets,
  leads,
  quotes,
  jobs,
  invoices,
  onOpenSales,
  onOpenJobs
}: {
  clients: ClientDTO[];
  targets: ServiceTargetDTO[];
  leads: LeadDTO[];
  quotes: QuoteDTO[];
  jobs: JobDTO[];
  invoices: InvoiceDTO[];
  onOpenSales: () => void;
  onOpenJobs: () => void;
}) {
  const openValue = quotes.filter((quote) => quote.status === "draft" || quote.status === "sent").reduce((total, quote) => total + quote.totalCents, 0);
  const hasSetup = clients.length > 0 || targets.length > 0 || leads.length > 0 || quotes.length > 0 || jobs.length > 0;
  const showGettingStarted = jobs.length === 0 && (clients.length === 0 || targets.length === 0 || leads.length === 0 || quotes.length === 0);
  const todayRoutes = useMemo(() => buildTodayRoutes({ clients, targets, leads, quotes, jobs, invoices }), [clients, targets, leads, quotes, jobs, invoices]);
  const visibleRoutes = todayRoutes.slice(0, 3);
  const hiddenRoutes = Math.max(todayRoutes.length - visibleRoutes.length, 0);
  const nextStop = visibleRoutes[0] ?? null;
  const weatherLabel = visibleRoutes.some((route) => route.chipLabels.some((label) => /weather|storm|rain|heat/i.test(label))) ? "Weather watch" : "Weather OK";
  const nextStopTiming = nextStop ? formatRelativeTime(nextStop.sortStart) : "Ready now";
  const todayDateLabel = new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="grid gap-5">
      <section className="grid gap-4 rounded-panel border border-border/50 bg-surface/96 p-4 shadow-soft sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-pill border border-border/40 bg-surface-soft px-3 py-1.5 text-xs font-extrabold uppercase tracking-[0.14em] text-primary">
              <Sparkles className="size-3.5" aria-hidden="true" />
              {todayDateLabel}
            </div>
            <h2 className="mt-3 text-[2rem] font-black tracking-[-0.04em] text-text sm:text-[2.35rem]">Route plan</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted">
              Keep the next stop visible, keep follow-up close, and keep the field day readable from one narrow screen.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <StatusPill tone={weatherLabel === "Weather OK" ? "success" : "warning"}>{weatherLabel}</StatusPill>
            <StatusPill tone="info">{nextStop ? nextStop.windowLabel : "No stops scheduled"}</StatusPill>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric icon={Clock3} label="Next up" value={nextStopTiming} />
          <Metric icon={CalendarDays} label="Stops" value={todayRoutes.filter((route) => route.kind === "job").length} />
          <Metric icon={CreditCard} label="Open value" value={formatMoney(openValue)} />
          <Metric icon={ClipboardList} label="Leads" value={leads.length} />
        </div>
      </section>

      <section className="grid gap-3">
        {visibleRoutes.length > 0 ? visibleRoutes.map((route) => (
          <RouteCard
            key={route.id}
            route={route}
            onOpenSales={onOpenSales}
            onOpenJobs={onOpenJobs}
          />
        )) : (
          <div className="rounded-panel border border-dashed border-border bg-surface-soft p-5">
            <p className="text-sm font-extrabold text-text">No route stops yet.</p>
            <p className="mt-1 text-sm leading-6 text-muted">Add a client, vehicle, and scheduled job to populate the Today route cards.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="secondary" icon={<Users className="size-4" aria-hidden="true" />} onClick={onOpenSales}>
                Open Sales
              </Button>
              <Button icon={<BriefcaseBusiness className="size-4" aria-hidden="true" />} onClick={onOpenJobs}>
                Open Jobs
              </Button>
            </div>
          </div>
        )}

        {hiddenRoutes > 0 ? (
          <div className="rounded-token border border-border/60 bg-surface-soft px-4 py-3 text-sm font-bold text-muted">
            {hiddenRoutes} more stop{hiddenRoutes === 1 ? "" : "s"} available in Jobs.
          </div>
        ) : null}
      </section>

      <section className="grid gap-3 border-t border-border/45 pt-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Button variant="secondary" icon={<Users className="size-4" aria-hidden="true" />} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Review route
          </Button>
          <Button variant="secondary" icon={<MessageCircle className="size-4" aria-hidden="true" />} onClick={onOpenSales}>
            Open Sales
          </Button>
          <Button icon={<CheckCircle2 className="size-4" aria-hidden="true" />} onClick={onOpenJobs}>
            Open Jobs
          </Button>
        </div>
        <div className="grid gap-2">
          <WorkflowRow icon={MessageCircle} label="Quote pipeline" value={`${quotes.length} quote${quotes.length === 1 ? "" : "s"}`} />
          <WorkflowRow icon={Camera} label="Booked jobs" value={`${jobs.length} job${jobs.length === 1 ? "" : "s"}`} />
          <WorkflowRow icon={Users} label="Vehicle profiles" value={`${targets.length} target${targets.length === 1 ? "" : "s"}`} />
        </div>
      </section>

      {showGettingStarted ? (
        <div className="grid gap-5 border-t border-border/45 pt-4">
          <section className="grid gap-3">
            <h2 className="text-base font-extrabold text-text">Flow</h2>
            <div className="grid gap-2 sm:grid-cols-4">
              <FlowStep title="1. Clients" detail="Store the customer" icon={Users} />
              <FlowStep title="2. Vehicle" detail="Track what you service" icon={Car} />
              <FlowStep title="3. Sales" detail="Turn requests into quotes" icon={MessageCircle} />
              <FlowStep title="4. Jobs" detail="Run accepted work" icon={BriefcaseBusiness} />
            </div>
          </section>

          <section className="grid gap-3 border-t border-border/45 pt-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-extrabold text-text">First 5 minutes</h2>
              {hasSetup ? <span className="rounded-pill bg-surface-soft px-3 py-1 text-xs font-extrabold text-primary">In progress</span> : null}
            </div>
            <div className="grid gap-2">
              <GuideRow
                step="1"
                title="Add your first client"
                detail="Start in Clients with the person who pays you."
                status={clients.length > 0 ? `${clients.length} added` : "Start here"}
              />
              <GuideRow
                step="2"
                title="Attach a vehicle"
                detail="Add the service target and reuse the saved address when needed."
                status={targets.length > 0 ? `${targets.length} saved` : "Next"}
              />
              <GuideRow
                step="3"
                title="Create a lead in Sales"
                detail="A lead is the incoming request before it becomes a quote."
                status={leads.length > 0 ? `${leads.length} open` : "Then this"}
              />
              <GuideRow
                step="4"
                title="Build and accept a quote"
                detail="Choose a package, add details, then send or accept it."
                status={quotes.length > 0 ? `${quotes.length} quote${quotes.length === 1 ? "" : "s"}` : "After lead"}
              />
            </div>
          </section>

          <section className="grid gap-3 border-t border-border/45 pt-4">
            <h2 className="text-base font-extrabold text-text">What each tab is for</h2>
            <div className="grid gap-2">
              <TabGuideRow icon={Home} title="Today" detail="See the route plan and what should happen next." />
              <TabGuideRow icon={Users} title="Clients" detail="Manage customers and vehicles." />
              <TabGuideRow icon={MessageCircle} title="Sales" detail="Create leads, build quotes, and convert accepted work." />
              <TabGuideRow icon={BriefcaseBusiness} title="Jobs" detail="See work that is already booked and ready to run." />
            </div>
          </section>
        </div>
      ) : (
        <div className="grid gap-5 border-t border-border/45 pt-4">
          <section className="grid gap-3">
            <h2 className="text-base font-extrabold text-text">Keep the loop moving</h2>
            <div className="grid gap-2">
              <FocusRow
                icon={MessageCircle}
                title="Open quotes need a next move"
                detail="Use Sales to finish drafts, send pending quotes, and convert confirmed work into jobs."
                status={quotes.filter((quote) => quote.status === "draft" || quote.status === "sent").length === 0
                  ? "Clear"
                  : `${quotes.filter((quote) => quote.status === "draft" || quote.status === "sent").length} live`}
              />
              <FocusRow
                icon={BriefcaseBusiness}
                title="Booked work belongs in Jobs"
                detail="Accepted quotes are already scheduled work. Open Jobs when the customer says yes."
                status={jobs.length === 0 ? "Waiting" : `${jobs.length} ready`}
              />
              <FocusRow
                icon={Users}
                title="Client records should stay reusable"
                detail="Keep the customer and vehicle clean so the next quote starts from known information."
                status={clients.length === 0 ? "Start" : `${clients.length} saved`}
              />
            </div>
          </section>

          <section className="grid gap-3 border-t border-border/45 pt-4">
            <h2 className="text-base font-extrabold text-text">Where to jump next</h2>
            <div className="grid gap-2">
              <TabGuideRow icon={MessageCircle} title="Sales" detail="Open this when the customer request still needs pricing, timing, or confirmation." />
              <TabGuideRow icon={BriefcaseBusiness} title="Jobs" detail="Open this when the work is already booked and the field day needs to run." />
              <TabGuideRow icon={Users} title="Clients" detail="Open this when the person, vehicle, or service address needs cleanup before the next quote." />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function RouteCard({
  route,
  onOpenSales,
  onOpenJobs
}: {
  route: TodayRouteItem;
  onOpenSales: () => void;
  onOpenJobs: () => void;
}) {
  const navigateUrl = route.address ? buildMapsDirectionsUrl(route.address) : null;

  return (
    <article className="rounded-panel border border-border/50 bg-surface p-4 shadow-elevated">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-extrabold uppercase tracking-[0.12em] text-primary">{route.orderLabel}</p>
          <p className="mt-1 text-xs font-bold text-muted">{route.windowLabel}</p>
        </div>
        <StatusPill tone={route.statusTone}>{route.statusLabel}</StatusPill>
      </div>

      <div className="mt-4 grid gap-1.5">
        <p className="text-lg font-extrabold leading-tight text-text">{route.customerName}</p>
        <p className="text-sm font-medium text-muted">{route.vehicleLabel}</p>
        <p className="text-sm font-bold text-text">{route.title}</p>
      </div>

      <div className="mt-4 grid gap-2 rounded-token border border-border/45 bg-surface-soft p-3">
        <div className="grid gap-1">
          <p className="text-sm font-bold text-text">{route.locationLabel}</p>
          <p className="text-sm font-medium text-muted">{route.contextLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-text">
          <span>{route.valueLabel}</span>
          <span className="text-muted">·</span>
          <span>{route.kind === "job" ? "Job" : "Follow-up"}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {route.chipLabels.map((chip) => (
            <span key={chip} className="rounded-pill border border-border/50 bg-surface px-2.5 py-1 text-xs font-extrabold text-muted">
              {chip}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {route.primaryAction.kind === "navigate" ? (
          <Button
            className="min-h-11 px-3 py-2 text-xs"
            icon={<Navigation2 className="size-4" aria-hidden="true" />}
            disabled={!navigateUrl}
            onClick={() => {
              if (!navigateUrl) return;
              window.open(navigateUrl, "_blank", "noopener,noreferrer");
            }}
          >
            {route.primaryAction.label}
          </Button>
        ) : null}
        {route.primaryAction.kind === "open-sales" ? (
          <Button
            className="min-h-11 px-3 py-2 text-xs"
            icon={<MessageCircle className="size-4" aria-hidden="true" />}
            onClick={onOpenSales}
          >
            {route.primaryAction.label}
          </Button>
        ) : null}
        {route.primaryAction.kind === "open-jobs" ? (
          <Button
            className="min-h-11 px-3 py-2 text-xs"
            icon={<BriefcaseBusiness className="size-4" aria-hidden="true" />}
            onClick={onOpenJobs}
          >
            {route.primaryAction.label}
          </Button>
        ) : null}

        {route.secondaryActions.map((action) => {
          if (action.kind === "call") {
            return (
              <InlineAction
                key={`${route.id}:${action.kind}`}
                icon={Phone}
                label={action.label}
                disabled={!route.contactPhone}
                onClick={() => {
                  if (!route.contactPhone) return;
                  window.location.href = `tel:${route.contactPhone}`;
                }}
              />
            );
          }

          if (action.kind === "text") {
            return (
              <InlineAction
                key={`${route.id}:${action.kind}`}
                icon={MessageCircle}
                label={action.label}
                disabled={!route.contactPhone}
                onClick={() => {
                  if (!route.contactPhone) return;
                  window.location.href = `sms:${route.contactPhone}`;
                }}
              />
            );
          }

          if (action.kind === "note") {
            return <InlineAction key={`${route.id}:${action.kind}`} icon={ClipboardList} label={action.label} onClick={onOpenJobs} />;
          }

          if (action.kind === "reorder") {
            return <InlineAction key={`${route.id}:${action.kind}`} icon={ArrowUpDown} label={action.label} onClick={onOpenJobs} />;
          }

          if (action.kind === "open-sales") {
            return <InlineAction key={`${route.id}:${action.kind}`} icon={MessageCircle} label={action.label} onClick={onOpenSales} />;
          }

          return <InlineAction key={`${route.id}:${action.kind}`} icon={BriefcaseBusiness} label={action.label} onClick={onOpenJobs} />;
        })}
      </div>
    </article>
  );
}

function InlineAction({
  icon: Icon,
  label,
  onClick,
  disabled = false
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      className="inline-flex min-h-10 items-center gap-1.5 rounded-pill border border-border/70 bg-elevated px-3 py-2 text-xs font-extrabold text-text transition hover:bg-surface-soft disabled:cursor-not-allowed disabled:opacity-50"
      type="button"
      disabled={disabled}
      onClick={onClick}
    >
      <Icon className="size-3.5 text-primary" aria-hidden="true" />
      {label}
    </button>
  );
}

function StatusPill({ tone, children }: { tone: "success" | "warning" | "info"; children: ReactNode }) {
  const classes = {
    success: "border-success/20 bg-success/10 text-success",
    warning: "border-warning/20 bg-warning/10 text-warning",
    info: "border-info/20 bg-info/10 text-info"
  };

  return <span className={`rounded-pill border px-2.5 py-1 text-xs font-extrabold ${classes[tone]}`}>{children}</span>;
}

function buildTodayRoutes({
  clients,
  targets,
  leads,
  quotes,
  jobs,
  invoices
}: {
  clients: ClientDTO[];
  targets: ServiceTargetDTO[];
  leads: LeadDTO[];
  quotes: QuoteDTO[];
  jobs: JobDTO[];
  invoices: InvoiceDTO[];
}): TodayRouteItem[] {
  const clientsById = new Map(clients.map((client) => [client.id, client]));
  const targetsById = new Map(targets.map((target) => [target.id, target]));
  const leadsById = new Map(leads.map((lead) => [lead.id, lead]));
  const invoicesByJobId = new Map(invoices.map((invoice) => [invoice.jobId, invoice]));

  const sortedJobs = [...jobs].sort((left, right) => compareScheduled(left.scheduledStart, right.scheduledStart) || left.title.localeCompare(right.title));
  const jobRoutes = sortedJobs.map((job, index) => buildJobRoute(job, invoicesByJobId.get(job.id) ?? null, index + 1, clientsById, targetsById));

  const openQuote = [...quotes]
    .filter((quote) => quote.status === "draft" || quote.status === "sent")
    .sort((left, right) => compareScheduled(left.scheduledStart, right.scheduledStart) || right.totalCents - left.totalCents)[0];

  const openLead = [...leads]
    .filter((lead) => lead.status !== "won" && lead.status !== "converted" && lead.status !== "closed")
    .sort((left, right) => compareScheduled(left.scheduledAt, right.scheduledAt) || left.summary.localeCompare(right.summary))[0];

  const followUpRoute = openQuote
    ? buildQuoteRoute(openQuote, jobRoutes.length + 1, clientsById, targetsById, leadsById)
    : openLead
      ? buildLeadRoute(openLead, jobRoutes.length + 1, clientsById, targetsById)
      : null;

  return followUpRoute ? [...jobRoutes, followUpRoute] : jobRoutes;
}

function buildJobRoute(
  job: JobDTO,
  invoice: InvoiceDTO | null,
  index: number,
  clientsById: Map<string, ClientDTO>,
  targetsById: Map<string, ServiceTargetDTO>
): TodayRouteItem {
  const client = job.clientId ? clientsById.get(job.clientId) ?? null : null;
  const target = job.targetId ? targetsById.get(job.targetId) ?? null : null;
  const address = target?.address ?? null;
  const notes = job.notes?.trim() ?? "";
  const statusLabel = routeStatusLabel(job.status);
  const statusTone = routeStatusTone(job.status);
  const primaryAction = job.status.includes("progress")
    ? { label: "Complete job", kind: "open-jobs" as const }
    : address
      ? { label: "Navigate", kind: "navigate" as const }
      : { label: "Open job", kind: "open-jobs" as const };

  return {
    id: `job-${job.id}`,
    kind: "job",
    orderLabel: `#${index}`,
    title: job.title,
    customerName: client?.name ?? "Unassigned client",
    vehicleLabel: describeVehicle(target),
    locationLabel: address ?? "Address pending",
    windowLabel: formatScheduleWindow(job.scheduledStart, job.scheduledEnd),
    contextLabel: invoice ? invoiceContextLabel(invoice, notes) : notes || "Travel time pending",
    valueLabel: invoice ? formatMoney(invoice.amountCents) : job.priceCents ? formatMoney(job.priceCents) : "Price pending",
    statusLabel,
    statusTone,
    chipLabels: routeRiskChips(job.status, notes, address, invoice),
    contactPhone: client?.phone ?? null,
    address,
    sortStart: job.scheduledStart,
    primaryAction,
    secondaryActions: [
      { label: "Call", kind: "call" },
      { label: "Text", kind: "text" },
      { label: "Note", kind: "note" },
      { label: "Reorder", kind: "reorder" }
    ]
  };
}

function buildQuoteRoute(
  quote: QuoteDTO,
  index: number,
  clientsById: Map<string, ClientDTO>,
  targetsById: Map<string, ServiceTargetDTO>,
  leadsById: Map<string, LeadDTO>
): TodayRouteItem {
  const lead = leadsById.get(quote.leadId) ?? null;
  const client = quote.clientId ? clientsById.get(quote.clientId) ?? null : null;
  const target = quote.targetId ? targetsById.get(quote.targetId) ?? null : null;
  const contextLabel = quote.notes?.trim() || lead?.summary || "Nearby lead";

  return {
    id: `quote-${quote.id}`,
    kind: "follow-up",
    orderLabel: `#${index}`,
    title: quote.title,
    customerName: client?.name ?? "Quote follow-up",
    vehicleLabel: describeVehicle(target),
    locationLabel: target?.address ?? "Address pending",
    windowLabel: lead?.scheduledAt ? `Follow-up ${formatTimeOnly(lead.scheduledAt)}` : "Send before 4:00 PM",
    contextLabel,
    valueLabel: formatMoney(quote.totalCents),
    statusLabel: "Smart",
    statusTone: "info",
    chipLabels: ["Quote pending", quote.status.replaceAll("_", " ")],
    contactPhone: client?.phone ?? null,
    address: target?.address ?? null,
    sortStart: lead?.scheduledAt ?? quote.scheduledStart,
    primaryAction: { label: "Open Sales", kind: "open-sales" },
    secondaryActions: [
      { label: "Call", kind: "call" },
      { label: "Text", kind: "text" },
      { label: "Open lead", kind: "open-sales" }
    ]
  };
}

function buildLeadRoute(
  lead: LeadDTO,
  index: number,
  clientsById: Map<string, ClientDTO>,
  targetsById: Map<string, ServiceTargetDTO>
): TodayRouteItem {
  const client = lead.clientId ? clientsById.get(lead.clientId) ?? null : null;
  const target = lead.targetId ? targetsById.get(lead.targetId) ?? null : null;
  const contextLabel = lead.source.trim() || "Lead follow-up";

  return {
    id: `lead-${lead.id}`,
    kind: "follow-up",
    orderLabel: `#${index}`,
    title: lead.summary,
    customerName: client?.name ?? "Lead follow-up",
    vehicleLabel: describeVehicle(target),
    locationLabel: target?.address ?? "Address pending",
    windowLabel: lead.scheduledAt ? `Follow-up ${formatTimeOnly(lead.scheduledAt)}` : "Send before 4:00 PM",
    contextLabel,
    valueLabel: lead.quotedCents ? formatMoney(lead.quotedCents) : "Quote pending",
    statusLabel: "Smart",
    statusTone: "info",
    chipLabels: ["Nearby lead", lead.status.replaceAll("_", " ")],
    contactPhone: client?.phone ?? null,
    address: target?.address ?? null,
    sortStart: lead.scheduledAt,
    primaryAction: { label: "Open Sales", kind: "open-sales" },
    secondaryActions: [
      { label: "Call", kind: "call" },
      { label: "Text", kind: "text" },
      { label: "Open lead", kind: "open-sales" }
    ]
  };
}

function routeStatusLabel(status: string) {
  if (/progress|active/i.test(status)) return "Live";
  if (/complete|done|paid/i.test(status)) return "Done";
  if (/late|hold|blocked|issue/i.test(status)) return "Watch";
  return "Ready";
}

function routeStatusTone(status: string): "success" | "warning" | "info" {
  if (/progress|active|done|paid/i.test(status)) return "success";
  if (/late|hold|blocked|issue/i.test(status)) return "warning";
  return "info";
}

function routeRiskChips(status: string, notes: string, address: string | null, invoice: InvoiceDTO | null) {
  const chips: string[] = [];
  const noteText = `${status} ${notes} ${address ?? ""}`.toLowerCase();
  if (noteText.includes("weather") || noteText.includes("rain") || noteText.includes("storm") || noteText.includes("heat")) {
    chips.push("Weather");
  }
  if (noteText.includes("parking") || noteText.includes("gate") || noteText.includes("access")) {
    chips.push("Access note");
  }
  if (invoice?.status === "sent") {
    chips.push("Payment due");
  } else if (invoice?.status === "paid") {
    chips.push("Paid");
  } else if (invoice?.status === "draft") {
    chips.push("Invoice draft");
  } else if (noteText.includes("payment") || noteText.includes("invoice")) {
    chips.push("Payment due");
  }
  if (noteText.includes("follow") || noteText.includes("quote")) {
    chips.push("Follow-up");
  }
  return chips;
}

function invoiceContextLabel(invoice: InvoiceDTO, notes: string) {
  const invoiceStatus = formatInvoiceStatus(invoice.status);
  if (invoice.status === "paid" && invoice.paidAt) {
    return `${invoiceStatus} ${formatRelativeDay(invoice.paidAt)}`;
  }
  if (invoice.status === "sent" && invoice.sentAt) {
    return `${invoiceStatus} ${formatRelativeDay(invoice.sentAt)}`;
  }
  return notes || invoiceStatus;
}

function describeVehicle(target: ServiceTargetDTO | null) {
  if (!target) return "Vehicle pending";
  const details = target.details ?? {};
  const year = stringValue(details["year"]);
  const make = stringValue(details["make"]);
  const model = stringValue(details["model"]);
  const descriptor = [year, make, model].filter(Boolean).join(" ").trim();
  return descriptor || target.label || "Vehicle pending";
}

function formatScheduleWindow(start: string | null, end: string | null) {
  if (!start && !end) return "Time TBD";
  if (start && end) return `${formatTimeOnly(start)}-${formatTimeOnly(end)}`;
  return formatTimeOnly(start ?? end);
}

function formatTimeOnly(value: string | null) {
  if (!value) return "TBD";
  return new Date(value).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatRelativeTime(value: string | null) {
  if (!value) return "Ready now";
  const now = Date.now();
  const target = new Date(value).getTime();
  const diffMins = Math.round((target - now) / 60000);
  if (Number.isNaN(diffMins)) return "Ready now";
  if (diffMins <= 0) return "Ready now";
  if (diffMins < 60) return `${diffMins} min`;
  const hours = Math.floor(diffMins / 60);
  const minutes = diffMins % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function compareScheduled(left: string | null, right: string | null) {
  if (left && right) return new Date(left).getTime() - new Date(right).getTime();
  if (left) return -1;
  if (right) return 1;
  return 0;
}

function buildMapsDirectionsUrl(address: string) {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function JobsPanel({
  jobs,
  invoices,
  clients,
  targets,
  organization,
  messageTemplates,
  onRefresh
}: {
  jobs: JobDTO[];
  invoices: InvoiceDTO[];
  clients: ClientDTO[];
  targets: ServiceTargetDTO[];
  organization: OrganizationDTO;
  messageTemplates: MessageTemplateDTO[];
  onRefresh: () => Promise<void>;
}) {
  const [selectedJobId, setSelectedJobId] = useState(jobs[0]?.id ?? "");
  const selectedJob = jobs.find((job) => job.id === selectedJobId) ?? jobs[0] ?? null;
  const invoicesByJobId = useMemo(() => new Map(invoices.map((invoice) => [invoice.jobId, invoice])), [invoices]);
  const clientsById = useMemo(() => new Map(clients.map((client) => [client.id, client])), [clients]);
  const targetsById = useMemo(() => new Map(targets.map((target) => [target.id, target])), [targets]);

  useEffect(() => {
    if (jobs.length === 0) {
      setSelectedJobId("");
      return;
    }
    if (!selectedJobId || !jobs.some((job) => job.id === selectedJobId)) {
      setSelectedJobId(jobs[0]!.id);
    }
  }, [jobs, selectedJobId]);

  return (
    <div className="grid gap-5">
      <SectionHeader icon={BriefcaseBusiness} title="Jobs" description="Run booked work and keep proof tied to the job record." />
      {jobs.length === 0 ? <Empty icon={BriefcaseBusiness} label="No jobs scheduled" /> : (
        <div className="grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
          <section className="grid gap-2">
            {jobs.map((job) => (
              <button
                key={job.id}
                className={`rounded-token border p-3 text-left transition ${
                  selectedJob?.id === job.id ? "border-primary bg-surface-soft" : "border-border/50 bg-surface hover:bg-surface-soft"
                }`}
                type="button"
                onClick={() => setSelectedJobId(job.id)}
              >
                <p className="font-extrabold text-text">{job.title}</p>
                <p className="mt-1 text-sm font-medium text-muted">
                  {job.status}{job.scheduledStart ? ` · ${formatDateTime(job.scheduledStart)}` : ""}{job.priceCents ? ` · ${formatMoney(job.priceCents)}` : ""}
                </p>
                {invoicesByJobId.get(job.id) ? (
                  <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">
                    Invoice {formatInvoiceStatus(invoicesByJobId.get(job.id)!.status)}
                  </p>
                ) : null}
              </button>
            ))}
          </section>

          {selectedJob ? (
            <JobDetailPanel
              job={selectedJob}
              invoice={invoicesByJobId.get(selectedJob.id) ?? null}
              client={selectedJob.clientId ? clientsById.get(selectedJob.clientId) ?? null : null}
              target={selectedJob.targetId ? targetsById.get(selectedJob.targetId) ?? null : null}
              organization={organization}
              messageTemplates={messageTemplates}
              onRefresh={onRefresh}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function JobDetailPanel({
  job,
  invoice,
  client,
  target,
  organization,
  messageTemplates,
  onRefresh
}: {
  job: JobDTO;
  invoice: InvoiceDTO | null;
  client: ClientDTO | null;
  target: ServiceTargetDTO | null;
  organization: OrganizationDTO;
  messageTemplates: MessageTemplateDTO[];
  onRefresh: () => Promise<void>;
}) {
  return (
    <section className="grid gap-4 rounded-panel border border-border/50 bg-surface p-4 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">Job detail</p>
          <h2 className="mt-2 text-xl font-extrabold text-text">{job.title}</h2>
          <p className="mt-1 text-sm leading-6 text-muted">
            {job.status}{job.scheduledStart ? ` · ${formatDateTime(job.scheduledStart)}` : " · Time TBD"}
          </p>
        </div>
        <StatusPill tone={routeStatusTone(job.status)}>{routeStatusLabel(job.status)}</StatusPill>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <DataRow icon={CreditCard} title={job.priceCents ? formatMoney(job.priceCents) : "Price pending"} detail="Quoted job value" />
        <DataRow icon={ClipboardList} title={job.notes ?? "No job notes"} detail={client ? `Client: ${client.name}` : "Operator notes"} />
      </div>

      <ChecklistPanel job={job} />
      <BillingPanel job={job} invoice={invoice} onRefresh={onRefresh} />
      <PhotoProofPanel jobId={job.id} />
      <QuickRepliesPanel job={job} client={client} target={target} organization={organization} templates={messageTemplates} />
    </section>
  );
}

function ChecklistPanel({ job }: { job: JobDTO }) {
  const [items, setItems] = useState<JobChecklistItemDTO[]>([]);
  const [newLabel, setNewLabel] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshChecklist = useCallback(async () => {
    try {
      setItems(await api.jobChecklist(job.id));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to load the checklist.");
    }
  }, [job.id]);

  useEffect(() => {
    setItems([]);
    setNewLabel("");
    setNewNotes("");
    setMessage("");
    void refreshChecklist();
  }, [job.id, refreshChecklist]);

  async function addItem() {
    if (!newLabel.trim()) {
      setMessage("Enter a checklist item before saving.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await api.createJobChecklistItem(job.id, {
        label: newLabel.trim(),
        notes: newNotes.trim() || null,
        sortOrder: items.length
      });
      setNewLabel("");
      setNewNotes("");
      await refreshChecklist();
      setMessage("Checklist item added.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to add a checklist item.");
    } finally {
      setBusy(false);
    }
  }

  async function saveStatus(item: JobChecklistItemDTO, status: "pending" | "completed" | "skipped") {
    setBusy(true);
    setMessage("");
    try {
      await api.updateJobChecklistItem(item.id, { status });
      await refreshChecklist();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update the checklist.");
    } finally {
      setBusy(false);
    }
  }

  async function removeItem(id: string) {
    setBusy(true);
    setMessage("");
    try {
      await api.deleteJobChecklistItem(id);
      await refreshChecklist();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to remove the checklist item.");
    } finally {
      setBusy(false);
    }
  }

  const completedCount = items.filter((item) => item.status === "completed").length;

  return (
    <section className="grid gap-4 border-t border-border/45 pt-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-extrabold text-text">Checklist</h3>
          <p className="mt-1 text-sm leading-6 text-muted">Work the field steps in one place and close the job with proof.</p>
        </div>
        <span className="rounded-pill bg-surface-soft px-3 py-1 text-xs font-extrabold text-primary">
          {completedCount}/{items.length || 0} done
        </span>
      </div>

      <div className="grid gap-3 rounded-token border border-border/45 bg-surface-soft p-3">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field
            label="New step"
            value={newLabel}
            onChange={(event) => setNewLabel(event.target.value)}
            placeholder="Capture wheel well before photos"
          />
          <TextareaField
            label="Step note"
            value={newNotes}
            onChange={(event) => setNewNotes(event.target.value)}
            placeholder="Optional operator note"
            rows={2}
          />
          <Button
            className="sm:self-end"
            type="button"
            variant="secondary"
            icon={<Plus className="size-4" aria-hidden="true" />}
            disabled={busy}
            onClick={() => { void addItem(); }}
          >
            Add step
          </Button>
        </div>

        <div className="grid gap-2">
          {items.map((item) => (
            <article key={item.id} className="grid gap-3 rounded-token border border-border/45 bg-surface p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-text">{item.label}</p>
                  <p className="mt-1 text-sm leading-6 text-muted">{item.notes ?? "No note for this step."}</p>
                </div>
                <StatusPill tone={item.status === "completed" ? "success" : item.status === "skipped" ? "warning" : "info"}>
                  {item.status === "completed" ? "Done" : item.status === "skipped" ? "Skipped" : "Pending"}
                </StatusPill>
              </div>
              <div className="grid gap-2 sm:grid-cols-4">
                <Button
                  type="button"
                  variant={item.status === "completed" ? "primary" : "secondary"}
                  disabled={busy}
                  onClick={() => { void saveStatus(item, item.status === "completed" ? "pending" : "completed"); }}
                >
                  {item.status === "completed" ? "Mark pending" : "Mark done"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => { void saveStatus(item, item.status === "skipped" ? "pending" : "skipped"); }}
                >
                  {item.status === "skipped" ? "Undo skip" : "Skip"}
                </Button>
                <div className="rounded-token border border-border/45 bg-surface-soft px-3 py-2 text-xs font-bold text-muted">
                  {item.completedAt ? `Completed ${formatDateTime(item.completedAt)}` : "Not completed yet"}
                </div>
                <Button type="button" variant="ghost" disabled={busy} onClick={() => { void removeItem(item.id); }}>
                  Remove
                </Button>
              </div>
            </article>
          ))}
          {items.length === 0 ? <Empty icon={ClipboardList} label="No checklist steps yet" /> : null}
        </div>
        {message ? <p className="text-sm font-medium text-muted">{message}</p> : null}
      </div>
    </section>
  );
}

function BillingPanel({ job, invoice, onRefresh }: { job: JobDTO; invoice: InvoiceDTO | null; onRefresh: () => Promise<void> }) {
  const [amountDollars, setAmountDollars] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAmountDollars(invoice ? centsToDollarsInput(invoice.amountCents) : job.priceCents ? centsToDollarsInput(job.priceCents) : "");
    setPaymentRef(invoice?.externalPaymentRef ?? "");
    setMessage("");
  }, [invoice, job.id, job.priceCents]);

  async function createInvoice() {
    const amountCents = dollarsInputToCents(amountDollars);
    if (amountCents <= 0) {
      setMessage("Enter the invoice amount before creating billing.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await api.createInvoiceForJob(job.id, {
        amountCents,
        externalPaymentRef: paymentRef.trim() || null
      });
      await onRefresh();
      setMessage("Invoice draft created.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to create invoice.");
    } finally {
      setBusy(false);
    }
  }

  async function saveInvoice(status?: "draft" | "sent" | "paid" | "void") {
    if (!invoice) return;
    const amountCents = dollarsInputToCents(amountDollars);
    if (amountCents <= 0) {
      setMessage("Enter the invoice amount before updating billing.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await api.updateInvoice(invoice.id, {
        amountCents,
        status,
        externalPaymentRef: paymentRef.trim() || null
      });
      await onRefresh();
      setMessage(status ? `Invoice ${formatInvoiceStatus(status).toLowerCase()}.` : "Billing saved.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update invoice.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="grid gap-4 border-t border-border/45 pt-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-extrabold text-text">Billing</h3>
          <p className="mt-1 text-sm leading-6 text-muted">Track whether this job has been invoiced and paid.</p>
        </div>
        <span className="rounded-pill bg-surface-soft px-3 py-1 text-xs font-extrabold text-primary">
          {invoice ? formatInvoiceStatus(invoice.status) : "No invoice"}
        </span>
      </div>

      <div className="grid gap-3 rounded-token border border-border/45 bg-surface-soft p-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Invoice amount"
            type="number"
            min="0"
            step="0.01"
            value={amountDollars}
            onChange={(event) => setAmountDollars(event.target.value)}
          />
          <Field
            label="Payment reference"
            value={paymentRef}
            onChange={(event) => setPaymentRef(event.target.value)}
            placeholder="Cash, Stripe, Square, Venmo"
          />
        </div>
        {invoice ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <DataRow
              icon={CreditCard}
              title={formatMoney(invoice.amountCents)}
              detail={invoice.status === "paid" && invoice.paidAt
                ? `Paid ${formatDateTime(invoice.paidAt)}`
                : invoice.status === "sent" && invoice.sentAt
                  ? `Sent ${formatDateTime(invoice.sentAt)}`
                  : `Status: ${formatInvoiceStatus(invoice.status)}`}
            />
            <DataRow
              icon={Clock3}
              title={invoice.externalPaymentRef ?? "No payment reference"}
              detail="Operator-entered payment method or tracking note"
            />
          </div>
        ) : null}
        <div className="grid gap-2 sm:grid-cols-2">
          {invoice ? (
            <>
              <Button variant="secondary" type="button" disabled={busy} onClick={() => { void saveInvoice(); }}>
                Save billing
              </Button>
              {invoice.status !== "paid" ? (
                <Button type="button" disabled={busy} onClick={() => { void saveInvoice(invoice.status === "sent" ? "paid" : "sent"); }}>
                  {invoice.status === "sent" ? "Mark paid" : "Mark sent"}
                </Button>
              ) : (
                <Button type="button" disabled={busy} onClick={() => { void saveInvoice("paid"); }}>
                  Mark paid
                </Button>
              )}
              <Button variant="secondary" type="button" disabled={busy} onClick={() => { void saveInvoice("draft"); }}>
                Set draft
              </Button>
              <Button variant="ghost" type="button" disabled={busy} onClick={() => { void saveInvoice("void"); }}>
                Void invoice
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" type="button" disabled={busy || dollarsInputToCents(amountDollars) <= 0} onClick={() => { void createInvoice(); }}>
                Create invoice
              </Button>
              <div />
            </>
          )}
        </div>
        {message ? <p className="text-sm font-medium text-muted">{message}</p> : null}
      </div>
    </section>
  );
}

function QuickRepliesPanel({
  job,
  client,
  target,
  organization,
  templates
}: {
  job: JobDTO;
  client: ClientDTO | null;
  target: ServiceTargetDTO | null;
  organization: OrganizationDTO;
  templates: MessageTemplateDTO[];
}) {
  const [message, setMessage] = useState("");
  const activeTemplates = useMemo(
    () => templates.filter((template) => template.status === "active"),
    [templates]
  );

  async function copyTemplate(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setMessage("Message copied.");
    } catch {
      setMessage("Unable to copy the message.");
    }
  }

  async function shareTemplate(text: string) {
    if (!navigator.share) {
      await copyTemplate(text);
      return;
    }
    try {
      await navigator.share({ text });
      setMessage("Share sheet opened.");
    } catch {
      setMessage("Share was canceled.");
    }
  }

  return (
    <section className="grid gap-4 border-t border-border/45 pt-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-extrabold text-text">Quick replies</h3>
          <p className="mt-1 text-sm leading-6 text-muted">Use saved reply templates for confirmations, reviews, and rebooking without rebuilding the text every time.</p>
        </div>
        <span className="rounded-pill bg-surface-soft px-3 py-1 text-xs font-extrabold text-primary">
          {activeTemplates.length} templates
        </span>
      </div>

      <div className="grid gap-3">
        {activeTemplates.map((template) => {
          const rendered = renderMessageTemplate(template.body, { job, client, target, organization });
          const needsReviewLink = template.templateKey === "review_request" && !organization.reviewLink;
          return (
            <article key={template.id} className="grid gap-3 rounded-token border border-border/45 bg-surface-soft p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-extrabold text-text">{template.title}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-primary">{formatTemplateKey(template.templateKey)}</p>
                </div>
                {needsReviewLink ? <StatusPill tone="warning">Add review link in Settings</StatusPill> : null}
              </div>
              <div className="rounded-token border border-border/45 bg-surface px-3 py-3 text-sm leading-6 text-text">
                {rendered}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Button type="button" variant="secondary" icon={<Copy className="size-4" aria-hidden="true" />} onClick={() => { void copyTemplate(rendered); }}>
                  Copy text
                </Button>
                <Button type="button" icon={<Share2 className="size-4" aria-hidden="true" />} onClick={() => { void shareTemplate(rendered); }}>
                  Share text
                </Button>
              </div>
            </article>
          );
        })}
        {activeTemplates.length === 0 ? <Empty icon={MessageCircle} label="No active message templates yet" /> : null}
        {message ? <p className="text-sm font-medium text-muted">{message}</p> : null}
      </div>
    </section>
  );
}

function PhotoProofPanel({ jobId }: { jobId: string }) {
  const [mediaAssets, setMediaAssets] = useState<MediaAssetDTO[]>([]);
  const [assetGroup, setAssetGroup] = useState("before");
  const [label, setLabel] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshMedia = useCallback(async () => {
    try {
      setMediaAssets(await api.jobMedia(jobId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to load photo proof.");
    }
  }, [jobId]);

  useEffect(() => {
    setMediaAssets([]);
    setMessage("");
    void refreshMedia();
  }, [jobId, refreshMedia]);

  async function uploadPhoto(file: File | null) {
    if (!file) return;
    setBusy(true);
    setMessage("");
    try {
      await api.uploadJobMedia(jobId, {
        file,
        assetGroup,
        label: label.trim() || undefined
      });
      setLabel("");
      await refreshMedia();
      setMessage("Photo added.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to upload photo.");
    } finally {
      setBusy(false);
    }
  }

  async function archivePhoto(id: string) {
    setBusy(true);
    setMessage("");
    try {
      await api.archiveMediaAsset(id);
      await refreshMedia();
      setMessage("Photo removed from active proof.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to remove photo.");
    } finally {
      setBusy(false);
    }
  }

  const grouped = mediaAssets.reduce<Record<string, MediaAssetDTO[]>>((accumulator, mediaAsset) => {
    (accumulator[mediaAsset.assetGroup] ??= []).push(mediaAsset);
    return accumulator;
  }, {});

  return (
    <section className="grid gap-4 border-t border-border/45 pt-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-extrabold text-text">Photo proof</h3>
          <p className="mt-1 text-sm leading-6 text-muted">Attach before, after, damage, or marketing photos to this job.</p>
        </div>
        <span className="rounded-pill bg-surface-soft px-3 py-1 text-xs font-extrabold text-primary">
          {mediaAssets.length} photo{mediaAssets.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid gap-3 rounded-token border border-border/45 bg-surface-soft p-3">
        <div className="grid gap-3 sm:grid-cols-[0.8fr_1fr]">
          <label className="grid gap-2 text-sm font-medium text-text">
            <span>Proof group</span>
            <select
              className="min-h-12 rounded-token border border-border/70 bg-elevated px-3 py-2 text-base text-text"
              value={assetGroup}
              onChange={(event) => setAssetGroup(event.target.value)}
            >
              <option value="before">Before</option>
              <option value="after">After</option>
              <option value="damage">Damage</option>
              <option value="marketing">Marketing</option>
            </select>
          </label>
          <Field label="Photo label" value={label} onChange={(event) => setLabel(event.target.value)} />
        </div>
        <label className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-token border border-border/70 bg-elevated px-4 py-3 text-sm font-extrabold text-text transition hover:bg-surface">
          <Upload className="size-4 text-primary" aria-hidden="true" />
          {busy ? "Uploading" : "Choose photo"}
          <input
            className="sr-only"
            type="file"
            accept="image/*"
            disabled={busy}
            onChange={(event) => {
              void uploadPhoto(event.target.files?.[0] ?? null);
              event.target.value = "";
            }}
          />
        </label>
        {message ? <p className="text-sm font-medium text-muted">{message}</p> : null}
      </div>

      {mediaAssets.length === 0 ? (
        <Empty icon={ImageIcon} label="No photo proof uploaded yet" />
      ) : (
        <div className="grid gap-4">
          {["before", "after", "damage", "marketing"].map((group) => {
            const items = grouped[group] ?? [];
            if (items.length === 0) return null;
            return (
              <div key={group} className="grid gap-2">
                <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">{group}</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {items.map((mediaAsset) => (
                    <ProofPhotoCard
                      key={mediaAsset.id}
                      mediaAsset={mediaAsset}
                      disabled={busy}
                      onArchive={() => {
                        void archivePhoto(mediaAsset.id);
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ProofPhotoCard({ mediaAsset, disabled, onArchive }: { mediaAsset: MediaAssetDTO; disabled: boolean; onArchive: () => void }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    void api.mediaAssetBlob(mediaAsset.id).then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }).catch(() => {
      if (active) setUrl("");
    });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaAsset.id]);

  return (
    <article className="overflow-hidden rounded-token border border-border/45 bg-surface">
      <div className="aspect-square bg-surface-soft">
        {url ? (
          <img className="size-full object-cover" src={url} alt={mediaAsset.label ?? mediaAsset.filename} />
        ) : (
          <div className="grid size-full place-items-center text-primary">
            <ImageIcon className="size-7" aria-hidden="true" />
          </div>
        )}
      </div>
      <div className="grid gap-2 p-2.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-extrabold text-text">{mediaAsset.label ?? mediaAsset.filename}</p>
          <p className="text-xs font-medium text-muted">{formatFileSize(mediaAsset.byteSize)}</p>
        </div>
        <button
          className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-token border border-border/60 bg-surface-soft px-2 py-1 text-xs font-extrabold text-text transition hover:bg-surface disabled:opacity-50"
          type="button"
          disabled={disabled}
          onClick={onArchive}
        >
          <Trash2 className="size-3.5 text-primary" aria-hidden="true" />
          Remove
        </button>
      </div>
    </article>
  );
}

function ProfilePanel({ me }: { me: AuthMeDTO }) {
  return (
    <div className="grid gap-3">
      <SectionHeader icon={User} title="User Profile" description="Account identity and access details." />
      <DataRow icon={UserCircle} title={me.user.email} detail={`Role: ${me.role}`} />
      <DataRow icon={Users} title={me.organization.name} detail="Primary workspace" />
    </div>
  );
}

function SettingsPanel({
  me,
  templates,
  onMeUpdated,
  onTemplatesUpdated
}: {
  me: AuthMeDTO;
  templates: MessageTemplateDTO[];
  onMeUpdated: (value: AuthMeDTO) => void;
  onTemplatesUpdated: (value: MessageTemplateDTO[]) => void;
}) {
  const [reviewLink, setReviewLink] = useState(me.organization.reviewLink ?? "");
  const [drafts, setDrafts] = useState<Record<string, { title: string; body: string }>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setReviewLink(me.organization.reviewLink ?? "");
  }, [me.organization.reviewLink]);

  useEffect(() => {
    setDrafts(Object.fromEntries(templates.map((template) => [template.id, {
      title: template.title,
      body: template.body
    }])));
  }, [templates]);

  async function saveSettings() {
    setBusy(true);
    setMessage("");
    try {
      await api.updateOrg({
        reviewLink: reviewLink.trim() || null
      });

      await Promise.all(templates.map((template) => {
        const draft = drafts[template.id];
        return api.updateMessageTemplate(template.id, {
          title: draft?.title?.trim() || template.title,
          body: draft?.body?.trim() || template.body
        });
      }));

      const [nextMe, nextTemplates] = await Promise.all([api.me(), api.messageTemplates()]);
      onMeUpdated(nextMe);
      onTemplatesUpdated(nextTemplates);
      setMessage("Follow-up settings saved.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save settings.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4">
      <SectionHeader icon={Settings} title="Settings" description="Current workspace identity, review link, and reusable reply templates." />
      <DataRow icon={Settings} title={me.user.email} detail={me.organization.vertical.replaceAll("_", " ")} />

      <section className="grid gap-3 rounded-panel border border-border/50 bg-surface p-4 shadow-soft">
        <div>
          <h2 className="text-base font-extrabold text-text">Follow-up setup</h2>
          <p className="mt-1 text-sm leading-6 text-muted">Set the review destination once, then adjust the default message copy your operators will reuse.</p>
        </div>
        <Field
          label="Review link"
          value={reviewLink}
          onChange={(event) => setReviewLink(event.target.value)}
          placeholder="https://g.page/r/..."
          hint="Used by the review request template. Leave blank if you are still collecting the right link."
        />
        <div className="rounded-token border border-border/45 bg-surface-soft px-3 py-3 text-xs font-bold leading-6 text-muted">
          Supported placeholders: {`{{client_first_name}}`} {`{{business_name}}`} {`{{business_phone}}`} {`{{job_title}}`} {`{{review_link}}`} {`{{scheduled_window}}`} {`{{service_target}}`}
        </div>
      </section>

      <section className="grid gap-3">
        {templates.map((template) => {
          const draft = drafts[template.id] ?? { title: template.title, body: template.body };
          return (
            <article key={template.id} className="grid gap-3 rounded-panel border border-border/50 bg-surface p-4 shadow-soft">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-extrabold text-text">{formatTemplateKey(template.templateKey)}</h2>
                  <p className="mt-1 text-sm leading-6 text-muted">This template appears in the Jobs quick-reply panel.</p>
                </div>
                <StatusPill tone={template.status === "active" ? "success" : "warning"}>
                  {template.status}
                </StatusPill>
              </div>
              <Field
                label="Template title"
                value={draft.title}
                onChange={(event) => setDrafts((current) => ({
                  ...current,
                  [template.id]: {
                    title: event.target.value,
                    body: current[template.id]?.body ?? template.body
                  }
                }))}
              />
              <TextareaField
                label="Template body"
                value={draft.body}
                onChange={(event) => setDrafts((current) => ({
                  ...current,
                  [template.id]: {
                    title: current[template.id]?.title ?? template.title,
                    body: event.target.value
                  }
                }))}
                rows={5}
              />
              <div className="rounded-token border border-border/45 bg-surface-soft px-3 py-3 text-sm leading-6 text-text">
                {renderMessageTemplate(draft.body, {
                  job: {
                    id: "preview",
                    organizationId: me.organization.id,
                    clientId: null,
                    targetId: null,
                    leadId: null,
                    title: "maintenance detail",
                    status: "scheduled",
                    scheduledStart: new Date().toISOString(),
                    scheduledEnd: null,
                    priceCents: null,
                    notes: null,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                  },
                  client: { id: "preview", organizationId: me.organization.id, name: "Jordan", email: null, phone: null, notes: null, status: "active", createdAt: "", updatedAt: "" },
                  target: { id: "preview", organizationId: me.organization.id, clientId: "preview", type: me.organization.vertical, label: "black SUV", address: null, details: {}, status: "active", createdAt: "", updatedAt: "" },
                  organization: me.organization
                })}
              </div>
            </article>
          );
        })}
      </section>

      <div className="grid gap-2 sm:grid-cols-[auto_1fr] sm:items-center">
        <Button type="button" disabled={busy} onClick={() => { void saveSettings(); }}>
          Save follow-up settings
        </Button>
        {message ? <p className="text-sm font-medium text-muted">{message}</p> : null}
      </div>
    </div>
  );
}

function AccountMenuButton({ icon: Icon, label, onClick }: { icon: LucideIcon; label: string; onClick: () => void }) {
  return (
    <button
      className="flex min-h-11 w-full items-center gap-3 rounded-token px-3 py-2 text-left text-sm font-extrabold text-text transition hover:bg-surface-soft"
      type="button"
      onClick={onClick}
    >
      <Icon className="size-4 text-primary" aria-hidden="true" />
      {label}
    </button>
  );
}

function SectionHeader({ icon: Icon, title, description }: { icon: LucideIcon; title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <IconMedallion icon={Icon} />
      <div>
        <h1 className="text-[2rem] font-black tracking-[-0.04em] text-text sm:text-[2.15rem]">{title}</h1>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-muted">{description}</p>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: number | string }) {
  return (
    <div className="rounded-token border border-border/50 bg-surface p-2.5 sm:p-3">
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <p className="text-xl font-extrabold text-text sm:text-2xl">{value}</p>
      </div>
      <p className="mt-1 text-xs font-bold text-muted sm:text-sm sm:font-medium">{label}</p>
    </div>
  );
}

function WorkflowRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="flex min-h-11 items-center justify-between gap-3 rounded-token border border-border/40 bg-surface-soft px-3 py-2">
      <span className="inline-flex items-center gap-2 text-sm font-extrabold">
        <Icon className="size-4 text-primary" aria-hidden="true" />
        {label}
      </span>
      <span className="text-xs font-bold text-muted">{value}</span>
    </div>
  );
}

function FlowStep({ icon: Icon, title, detail }: { icon: LucideIcon; title: string; detail: string }) {
  return (
    <div className="rounded-token border border-border/45 bg-surface px-3 py-3">
      <div className="flex items-center gap-2">
        <Icon className="size-4 text-primary" aria-hidden="true" />
        <p className="text-sm font-extrabold text-text">{title}</p>
      </div>
      <p className="mt-2 text-sm text-muted">{detail}</p>
    </div>
  );
}

function GuideRow({ step, title, detail, status }: { step: string; title: string; detail: string; status: string }) {
  return (
    <div className="flex items-start gap-3 rounded-token border border-border/40 bg-surface px-3 py-3">
      <span className="inline-grid size-8 shrink-0 place-items-center rounded-token bg-surface-soft text-sm font-extrabold text-primary">
        {step}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="font-extrabold text-text">{title}</p>
          <span className="shrink-0 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">{status}</span>
        </div>
        <p className="mt-1 text-sm text-muted">{detail}</p>
      </div>
    </div>
  );
}

function FocusRow({
  icon: Icon,
  title,
  detail,
  status
}: {
  icon: LucideIcon;
  title: string;
  detail: string;
  status: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-token border border-border/40 bg-surface px-3 py-3">
      <IconMedallion icon={Icon} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="font-extrabold text-text">{title}</p>
          <span className="shrink-0 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">{status}</span>
        </div>
        <p className="mt-1 text-sm text-muted">{detail}</p>
      </div>
    </div>
  );
}

function TabGuideRow({ icon: Icon, title, detail }: { icon: LucideIcon; title: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 rounded-token border border-border/40 bg-surface px-3 py-3">
      <IconMedallion icon={Icon} />
      <div>
        <p className="font-extrabold text-text">{title}</p>
        <p className="mt-1 text-sm text-muted">{detail}</p>
      </div>
    </div>
  );
}

function DataRow({ icon: Icon, title, detail }: { icon: LucideIcon; title: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 rounded-token border border-border/50 bg-surface p-3">
      <IconMedallion icon={Icon} />
      <div>
        <p className="font-extrabold">{title}</p>
        <p className="text-sm font-medium text-muted">{detail}</p>
      </div>
    </div>
  );
}

function TextareaField({
  label,
  hint,
  className = "",
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string }) {
  return (
    <label className="grid gap-2 text-sm text-text">
      <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-muted">{label}</span>
      <textarea
        className={`min-h-24 rounded-token border border-border/70 bg-elevated px-3 py-2.5 text-base font-medium text-text transition placeholder:text-muted/80 focus:border-primary focus:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/20 ${className}`}
        {...props}
      />
      {hint ? <span className="text-xs font-medium leading-5 text-muted">{hint}</span> : null}
    </label>
  );
}

function Empty({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-token border border-dashed border-border bg-surface-soft p-4 text-sm font-bold text-muted">
      <Icon className="size-5 text-primary" aria-hidden="true" />
      {label}
    </div>
  );
}

function IconMedallion({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="inline-grid size-10 shrink-0 place-items-center rounded-full border border-border/50 bg-surface-soft text-primary">
      <Icon className="size-5" aria-hidden="true" />
    </span>
  );
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(cents / 100);
}

function centsToDollarsInput(cents: number) {
  return (cents / 100).toFixed(2);
}

function dollarsInputToCents(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : 0;
}

function formatInvoiceStatus(status: string) {
  return status.replaceAll("_", " ");
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString();
}

function formatRelativeDay(value: string) {
  const date = new Date(value);
  const now = new Date();
  const diffDays = Math.round((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays > 1) return `${diffDays} days ago`;
  return "scheduled";
}

function formatVerticalLabel(value: string) {
  return value.replaceAll("_", " ");
}

function formatTemplateKey(value: string) {
  return value.replaceAll("_", " ");
}

function renderMessageTemplate(
  template: string,
  context: {
    job: JobDTO;
    client: ClientDTO | null;
    target: ServiceTargetDTO | null;
    organization: OrganizationDTO;
  }
) {
  const firstName = context.client?.name?.trim().split(/\s+/)[0] ?? "there";
  const replacements: Record<string, string> = {
    client_first_name: firstName,
    client_name: context.client?.name ?? "your customer",
    business_name: context.organization.name,
    business_phone: context.organization.phone ?? "your number",
    job_title: context.job.title,
    review_link: context.organization.reviewLink ?? "[add review link in Settings]",
    scheduled_window: context.job.scheduledStart ? formatDateTime(context.job.scheduledStart) : "your scheduled visit",
    service_target: context.target?.label ?? "your vehicle"
  };

  return template.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_, key: string) => replacements[key] ?? "");
}
